import debounce from 'lodash/debounce';
import { useEffect, useMemo, useState } from 'react';
import { useFormState } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import { funnelPayload } from '@appTypes/gtmFunnel';
import { VehicleObject } from '@appTypes/myVehiclesType';
import { useSendGTMEventOnce } from '@hooks/GTM/useSendGTMEventOnce';
import { useAuth0Token } from '@hooks/useAuth0Token';
import { useServiceCalculatorApi } from '@hooks/useServiceCalculatorApi';
import { useVehicleSearch } from '@hooks/useVehicleSearch';
import { actions as VehicleActions } from '@redux/modules/myVehicles';
import useSelector from '@redux/typedHooks';
import { normalizeLicensePlate } from '@utils/normalizeLicensePlate';
import { useTrackCTA } from '@utils/Tracking/CtaClick/TrackCtaClick';
import { useServiceCalculator } from '../../context/ServiceCalculatorContext';

interface SelectVehicleLogicOptions {
  nafCenterId: number;
  isActive?: boolean;
  currentStep?: number;
  goToStep?: (step: number) => void;
  updateProgress: (value: number) => void;
  updateProgressLabel: (value: string) => void;
}

export const useSelectVehicleStepLogic = ({
  nafCenterId,
  isActive,
  currentStep,
  goToStep,
  updateProgress,
  updateProgressLabel,
}: SelectVehicleLogicOptions) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const trackCTA = useTrackCTA();
  const { simpleToken } = useAuth0Token();
  const { vehicleList } = useSelector((state) => state.myVehicles);
  const [centers, setCenters] = useState<{ label: string; value: number }[]>([]);
  const [vehicles, setVehicles] = useState<{ label: string; value: string }[]>([]);
  const [vehicleLookup, setVehicleLookup] = useState('');
  const { vehicleInResponse, lookup } = useVehicleSearch();
  const { getAvailableCenters } = useServiceCalculatorApi();
  const { getOffer, getOfferWithPossibleEquipment, vehicleForm, workProposalForm, offer, resetOffer } =
    useServiceCalculator();
  const { isValid, isSubmitting, errors } = useFormState({ control: vehicleForm.control });
  const shouldBeSecondStep =
    offer.vehicleMaintenanceWizard?.workProposalGroups?.length > 0 && workProposalForm.getValues().workProposal !== -2;
  const isLoading = centers.length === 0 || vehicleList.isUpdating || (isValid && isSubmitting);

  const onSubmit = async () => {
    trackCTA({ cta_position: 'content', text: 'Neste' });
    if (!isValid) return;
    workProposalForm.reset();
    updateProgress(50);
    const values = vehicleForm.getValues();
    if (values.equipments.length > 0) await getOfferWithPossibleEquipment(values);
    else await getOffer(values);
  };

  const validateNafCenter = () =>
    centers.some((center) => center.value === vehicleForm.getValues().centerId) || 'Velg et senter.';

  const validateVehicle = () => {
    const { licensePlateNumber, vehicleName, isEv } = vehicleForm.getValues();
    const normalizedPlate = normalizeLicensePlate(licensePlateNumber);
    if (!licensePlateNumber) return 'Skriv inn bilens registreringsnummer.';
    if (vehicles.some((vehicle) => vehicle.value === normalizedPlate)) return true;
    if (vehicleName) return isEv || 'For øyeblikket kan vi kun estimere pris for elbiler.';
    return 'Vi fant ingen kjøretøy. Sørg for at registreringsnummer er korrekt, og prøv igjen.';
  };

  const handleLookup = useMemo(
    () =>
      debounce((inputValue: string) => {
        resetOffer();
        setVehicleLookup('');
        lookup({ token: simpleToken, licensePlateNumber: normalizeLicensePlate(inputValue) });
      }, 500),
    [lookup, resetOffer, simpleToken],
  );

  useEffect(() => () => handleLookup.cancel(), [handleLookup]);

  const handleChange = (label: string, value: string) => {
    resetOffer();
    const normalizedValue = normalizeLicensePlate(value);
    if (!vehicles.some((vehicle) => vehicle.value === normalizedValue)) return;
    vehicleForm.setValue('vehicleName', label?.split(' ').slice(1).join(' '));
    vehicleForm.setValue('isEv', true);
    vehicleForm.setValue('licensePlateNumber', normalizedValue);
    vehicleForm.clearErrors(['licensePlateNumber']);
  };

  useEffect(() => {
    if (!vehicleList.data && simpleToken) dispatch(VehicleActions.getMyVehicles.request(simpleToken));
  }, [vehicleList, simpleToken, dispatch]);

  useEffect(() => {
    const fetchCenters = async () => {
      const centerArray = await getAvailableCenters();
      if (centerArray) setCenters(centerArray.map((center) => ({ label: center.title, value: center.centerId })));
    };
    fetchCenters();
  }, [getAvailableCenters]);

  useEffect(() => {
    if (!vehicleList.data || vehicleList.isUpdating) return;
    const vehicleArray = Object.values(vehicleList.data) as VehicleObject[];
    setVehicles(
      vehicleArray
        .filter((vehicle) => vehicle?.isEv)
        .map((vehicle) => ({
          label: `${vehicle.licensePlateNumber} ${vehicle.manufactureName} ${vehicle.modelName}`,
          value: vehicle.licensePlateNumber,
        })),
    );
  }, [vehicleList]);

  useEffect(() => {
    const { licensePlateNumber } = vehicleForm.getValues();
    if (vehicleInResponse) {
      const name = `${vehicleInResponse.manufactureName} ${vehicleInResponse.modelName}`;
      setVehicleLookup(name);
      vehicleForm.setValue('vehicleName', name);
      vehicleForm.setValue('isEv', vehicleInResponse.isEv);
      vehicleForm.trigger();
    } else if (licensePlateNumber.length > 0) {
      vehicleForm.resetField('vehicleName');
      vehicleForm.resetField('isEv');
      vehicleForm.trigger();
    }
  }, [vehicleInResponse, vehicleForm]);

  useEffect(() => {
    if (!isActive) return;
    if (offer.vehicleMaintenanceWizard?.possibleEquipment.length > 0) updateProgress(50);
    else {
      updateProgress(25);
      updateProgressLabel('Velg senter og bil');
    }
  }, [isActive, updateProgress, updateProgressLabel, offer]);

  useEffect(() => {
    if (offer.vehicleMaintenanceWizard && !vehicleInResponse) {
      lookup({ token: simpleToken, licensePlateNumber: vehicleForm.getValues().licensePlateNumber });
    }
  }, [offer, vehicleInResponse, vehicleForm, lookup, simpleToken]);

  useEffect(() => {
    if (shouldBeSecondStep && currentStep !== undefined) goToStep?.(currentStep + 1);
  }, [shouldBeSecondStep, goToStep, currentStep]);

  useSendGTMEventOnce(funnelPayload('Servicekalkulator', 1), []);

  return {
    vehicleForm,
    errors,
    centers,
    vehicles,
    vehicleLookup,
    offer,
    isLoading,
    isSubmitting,
    shouldBeSecondStep,
    nafCenterId,
    onSubmit,
    validateNafCenter,
    validateVehicle,
    handleLookup,
    handleChange,
    goBack: () => {
      trackCTA({ cta_position: 'content', text: 'Tilbake' });
      navigate(-1);
    },
  };
};
